import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import useEntityIsCriticalAsset from "../../../../../api/hooks/useEntityIsCriticalAsset";
import useFullControl from "../../../../../api/hooks/useFullControl";
import useMSSP from "../../../../../api/hooks/useMSSP";
import {
  detectionKeys,
  platformUsersKeys,
  remediationKeys,
} from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import TenantAvatar from "../../../../../components/mssp/TenantAvatar";
import ResizableDrawer from "../../../../../components/ResizableDrawer";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import { DRAWER_TABS } from "../../../../../utils/constants";
import DetectionsTab from "../../../detections/components/DetectionsTab";
import { getActiveDetections } from "../../../detections/components/utils";
import ActivityTab from "./control/ActivityTab";
import ControlDetailsSection from "./control/ControlDetailsSection";
import ControlTab from "./control/ControlTab";
import RemediationTab from "./remediation/RemediationTab";

export default function ControlRemediationDrawer({
  open,
  onClose,
  selectedControl,
  selectedTab = DRAWER_TABS.CONTROL_TAB,
}) {
  const { remediationsDemoMode } = useContext(EnvFeatureFlagContext);
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const [activeTab, setActiveTab] = useState(selectedTab);
  const colors = useColors();
  const {
    fetchRemediation,
    trackEvent,
    getDetectionByControlID,
    getMSSPTenants,
  } = useAPI();
  const { entityIsCriticalAsset, getAssetByEntityId } =
    useEntityIsCriticalAsset();
  const { isMSSP, currentTenantId } = useMSSP();
  const [selectedTenant, setSelectedTenant] = useState(null);

  const [assetAffectedByDetection, setAssetAffectedByDetection] = useState([]);
  const scrollableContainerRef = useRef(null);

  const {
    fullControl,
    isLoading: isLoadingFullControl,
    isSuccess: isSuccessFullControl,
  } = useFullControl(selectedControl);

  const { data: tenants = [] } = useQuery({
    queryKey: platformUsersKeys.msspTenants(),
    queryFn: () => getMSSPTenants(),
    select: (response) => response.data,
    enabled: isMSSP,
  });

  useEffect(() => {
    if (isMSSP) {
      setSelectedTenant(
        tenants.find((tenant) => tenant.customer_id === currentTenantId)
      );
    }
  }, [isMSSP, currentTenantId, tenants]);

  const {
    data: selectedRemediation,
    isLoading: isLoadingRemediation,
    isSuccess: isSuccessRemediation,
  } = useQuery({
    queryKey: remediationKeys.remediation(
      selectedControl?.remediation?.remediation_id
    ),
    queryFn: () =>
      fetchRemediation(selectedControl?.remediation?.remediation_id),
    select: (response) => response.data || null,
    enabled:
      Boolean(selectedControl) &&
      Boolean(selectedControl?.remediation?.remediation_id),
  });

  const { data: detections, isLoading: isLoadingDetections } = useQuery({
    queryKey: detectionKeys.detectionsByControl(selectedControl?.control_id),
    queryFn: async () => {
      return await getDetectionByControlID(selectedControl.control_id);
    },
    select: (response) =>
      response?.data.sort((a, b) => {
        const dateA = a.date.timestamp_utc;
        const dateB = b.date.timestamp_utc;
        return dateB - dateA; // Sort in descending order
      }),
    enabled: selectedControl !== null,
  });
  const involvedCriticalAssets = (activeDetections) => {
    const assets = new Set();
    activeDetections?.forEach((detection) => {
      detection?.affected_entity_ids?.forEach((entity) => {
        if (entityIsCriticalAsset(entity)) {
          const asset = getAssetByEntityId(entity);
          if (asset && asset !== undefined) {
            assets.add(asset);
          }
        }
      });
    });

    return Array.from(assets);
  };

  useEffect(() => {
    if (detections) {
      const assets = involvedCriticalAssets(getActiveDetections(detections));

      if (assets.length > 0) {
        setAssetAffectedByDetection(assets);
      }
    }
  }, [detections]);

  const handleTrack = (tab) => {
    // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
    const trackMessage =
      tab === DRAWER_TABS.CONTROL_TAB
        ? "User opened drawer for control id "
        : tab === DRAWER_TABS.REMEDIATION_TAB
        ? "User opened drawer for remediation id "
        : tab === DRAWER_TABS.ACTIVITY_TAB
        ? "User opened activity tab for control id "
        : "";
    if (trackMessage) trackEvent(trackMessage + selectedControl?.control_id);
  };

  const HeaderControl = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="h5">
            {selectedControl?.name_for_gap_state}
          </Typography>

          <Typography variant="h5">#{selectedControl?.control_id}</Typography>
        </Box>

        <ControlDetailsSection
          fullControl={fullControl}
          demoMode={remediationsDemoMode}
        />
      </Box>
    );
  };
  useEffect(() => {
    if (open) {
      if (selectedControl) {
        handleTrack(selectedTab);
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedControl) {
      handleTrack(activeTab);
    }
  }, [activeTab]);

  return (
    <ResizableDrawer
      initialWidth={900}
      minWidth={900}
      anchor="right"
      open={open}
      onClose={() => {
        onClose();
        setActiveTab(selectedTab);
      }}
      PaperProps={{
        style: {
          backgroundColor: colors.grey_50,
          borderRadius: "8px 0 0 0",
        },
      }}
    >
      {isLoadingRemediation || isLoadingFullControl ? (
        <LoadingIcon />
      ) : (
        isSuccessRemediation &&
        isSuccessFullControl && (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: colors.primary_6percent,
                justifyContent: "space-between",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(event, newValue) => {
                  setActiveTab(newValue);
                }}
                textColor="inherit"
                sx={{
                  display: "flex",
                  flexDirection: "row",

                  "& .MuiTab-root": {
                    minWidth: 120,
                    textTransform: "none",
                    fontWeight: "bold",
                    padding: "10px 15px",
                  },
                  "& .Mui-selected": {
                    borderRadius: "8px 8px 0 0",
                    boxShadow:
                      "0px -1px 3px 0.5px  " + colors.primary_12percent,
                    backgroundColor: colors.grey_50,
                  },
                }}
                TabIndicatorProps={{
                  style: {
                    display: "none",
                    backgroundColor: colors.primary_main,
                  },
                }}
              >
                <Tab
                  value={DRAWER_TABS.CONTROL_TAB}
                  label={
                    <Typography
                      variant={
                        activeTab === DRAWER_TABS.CONTROL_TAB
                          ? "subtitle2"
                          : "body2"
                      }
                    >
                      Overview
                    </Typography>
                  }
                  sx={{
                    textTransform: "none",

                    paddingBottom: "0px",
                    marginBottom: "-4px",
                  }}
                />
                {detectionsActive &&
                  selectedControl?.last_detections.length > 0 && (
                    <Tab
                      value={DRAWER_TABS.DETECTION_TAB}
                      label={
                        <Typography
                          variant={
                            activeTab === DRAWER_TABS.DETECTION_TAB
                              ? "subtitle2"
                              : "body2"
                          }
                        >
                          Detection
                        </Typography>
                      }
                      sx={{
                        textTransform: "none",
                        paddingBottom: "0px",
                        marginBottom: "-4px",
                      }}
                    />
                  )}
                {selectedControl?.remediation?.remediation_id && (
                  <Tab
                    value={DRAWER_TABS.REMEDIATION_TAB}
                    label={
                      <Typography
                        variant={
                          activeTab === DRAWER_TABS.REMEDIATION_TAB
                            ? "subtitle2"
                            : "body2"
                        }
                      >
                        Remediation
                      </Typography>
                    }
                    sx={{
                      textTransform: "none",

                      paddingBottom: "0px",
                      marginBottom: "-4px",
                    }}
                  />
                )}
                <Tab
                  value={DRAWER_TABS.ACTIVITY_TAB}
                  label={
                    <Typography
                      variant={
                        activeTab === DRAWER_TABS.ACTIVITY_TAB
                          ? "subtitle2"
                          : "body2"
                      }
                    >
                      Activity
                    </Typography>
                  }
                  sx={{
                    textTransform: "none",

                    paddingBottom: "0px",
                    marginBottom: "-4px",
                  }}
                />
              </Tabs>
              {isMSSP && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1,
                    borderRadius: "6px",
                    backgroundColor: colors.common_white,
                    padding: "0 6px",
                    height: "24px !important",
                    justifyContent: "center",
                    marginRight: "16px",
                  }}
                >
                  {selectedTenant && <TenantAvatar tenant={selectedTenant} />}
                  <Typography variant="caption">
                    {selectedTenant?.company_name}
                  </Typography>
                </Box>
              )}
            </Box>
            <div
              ref={scrollableContainerRef}
              style={{
                overflowY: "auto",
                height: "calc(100vh - 50px)",
                padding: "0 24px",
                borderRadius: "8px 8px 0 0",
              }}
            >
              {activeTab === DRAWER_TABS.CONTROL_TAB && (
                <ControlTab
                  fullControl={fullControl}
                  setActiveTab={setActiveTab}
                  demoMode={remediationsDemoMode}
                  assetAffectedByDetection={assetAffectedByDetection}
                  scrollableContainerRef={scrollableContainerRef}
                />
              )}
              {activeTab === DRAWER_TABS.DETECTION_TAB &&
                detectionsActive &&
                selectedControl?.last_detections.length > 0 &&
                (!isLoadingDetections ? (
                  <DetectionsTab
                    detections={detections}
                    Header={HeaderControl}
                    handleMoveToControl={(tab = DRAWER_TABS.CONTROL_TAB) =>
                      setActiveTab(tab)
                    }
                  />
                ) : (
                  <LoadingIcon size={25} />
                ))}
              {activeTab === DRAWER_TABS.REMEDIATION_TAB &&
                selectedRemediation && (
                  <RemediationTab
                    selectedRemediation={selectedRemediation}
                    demoMode={remediationsDemoMode}
                    fullControl={fullControl}
                  />
                )}
              {activeTab === DRAWER_TABS.ACTIVITY_TAB && (
                <ActivityTab fullControl={fullControl} />
              )}
            </div>
          </Box>
        )
      )}
    </ResizableDrawer>
  );
}
